import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './TestList.css'
import LoadingScreen from '../components/LoadingScreen'
import alert from '../utils/alertService'

const TestList = () => {
  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [timeUpdates, setTimeUpdates] = useState({})
  const [faceRegistered, setFaceRegistered] = useState(false)
  const [checkingFace, setCheckingFace] = useState(true)
  const history = useHistory()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    checkFaceRegistration()
    fetchAvailableTests()
  }, [history])

  const checkFaceRegistration = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/face/check-registration', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setFaceRegistered(data.face_registered)
      }
    } catch (err) {
      console.error('Error checking face registration:', err)
    } finally {
      setCheckingFace(false)
    }
  }

  // Update countdown timers every second for tests that haven't started
  useEffect(() => {
    if (tests.length === 0) return

    const interval = setInterval(() => {
      setTests(prevTests => {
        let shouldRefresh = false
        const updatedTests = prevTests.map(test => {
          if (test.schedule_status === 'not_started' && test.time_until_start_seconds) {
            const newTimeRemaining = Math.max(0, test.time_until_start_seconds - 1)
            if (newTimeRemaining <= 0) {
              shouldRefresh = true
            }
            return {
              ...test,
              time_until_start_seconds: newTimeRemaining
            }
          }
          return test
        })
        
        if (shouldRefresh) {
          // Refresh test list when a test becomes available
          setTimeout(() => {
            fetchAvailableTests()
          }, 1000)
        }
        
        return updatedTests
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [tests.length])

  const fetchAvailableTests = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/student/available-tests', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setTests(data.tests)
      } else {
        setError('Failed to load tests')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleTakeTest = async (testId, test) => {
    // Payment gate: an unpaid, fee-based exam must be paid first
    if (test.payment_required && !test.is_paid) {
      alert.warning('This exam has a fee. Please pay using the challan before starting.', 5000)
      history.push(`/my-payments?testId=${testId}`)
      return
    }

    // Check if face registration is required for this test
    if (test.face_detection && !faceRegistered) {
      alert.warning('Face registration is required before taking this test.', 5000)
      history.push(`/face-registration?testId=${testId}&returnUrl=/available-tests`)
      return
    }

    history.push(`/test-taking/${testId}`)
  }

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return 'Now'
    
    const days = Math.floor(seconds / (24 * 60 * 60))
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60))
    const minutes = Math.floor((seconds % (60 * 60)) / 60)
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours > 1 ? 's' : ''}`
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`
    } else {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`
    }
  }

  const handleBackToDashboard = () => {
    history.push('/student-dashboard')
  }

  if (loading) {
    return <LoadingScreen message="Loading Available Tests" />
  }

  if (error) {
    return (
      <div className="test-list-container">
        <div className="error">{error}</div>
        <button onClick={handleBackToDashboard}>
          Back to Dashboard
        </button>
      </div>
    )
  }

  return (
    <div className="test-list-container">
      <header className="test-list-header">
        <div className="header-content">
          <h1>Available Tests</h1>
          <button 
            className="back-button"
            onClick={handleBackToDashboard}
          >
            ← Back to Dashboard
          </button>
        </div>
      </header>

      <main className="test-list-main">
        {tests.length === 0 ? (
          <div className="no-tests">
            <h2>No tests available</h2>
            <p>There are currently no active tests available for you to take.</p>
            <button onClick={handleBackToDashboard}>
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div className="tests-grid">
            {tests.map((test) => (
              <div key={test.id} className="test-card">
                <div className="test-card-header">
                  <h3>{test.title}</h3>
                  <div className="test-status">
                    {test.already_attempted ? (
                      <span className="status-attempted">Attempted</span>
                    ) : (test.payment_required && !test.is_paid) ? (
                      <span className="status-attempted" style={{ background: '#fbf0dc', color: '#b45309' }}>Fee due</span>
                    ) : (
                      <span className="status-available">Available</span>
                    )}
                  </div>
                </div>
                
                <div className="test-card-content">
                  <div className="test-content-top">
                    <p className="test-description">{test.description}</p>
                    
                    {/* Schedule Status Banner */}
                    {test.schedule_status === 'not_started' && (
                      <div className="schedule-banner schedule-not-started">
                        <span className="schedule-icon">⏰</span>
                        <span className="schedule-text">
                          Test starts in: <strong>{formatTimeRemaining(test.time_until_start_seconds)}</strong>
                        </span>
                      </div>
                    )}
                    
                    {test.schedule_status === 'ended' && (
                      <div className="schedule-banner schedule-ended">
                        <span className="schedule-icon">❌</span>
                        <span className="schedule-text">
                          Test time has passed
                        </span>
                      </div>
                    )}
                    
                    {test.schedule_status === 'available' && test.start_time && (
                      <div className="schedule-banner schedule-available">
                        <span className="schedule-icon">✅</span>
                        <span className="schedule-text">
                          Test is currently available
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="test-card-details">
                    <div className="detail-item">
                      <span className="detail-label">Duration</span>
                      <span className="detail-value">{test.duration_minutes} min</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Questions</span>
                      <span className="detail-value">{test.total_questions || 0}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Passing</span>
                      <span className="detail-value">{test.passing_score}%</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Points</span>
                      <span className="detail-value">{test.total_points || 0}</span>
                    </div>
                    {test.payment_required && (
                      <div className="detail-item">
                        <span className="detail-label">Fee</span>
                        <span className="detail-value">{test.is_paid ? 'Paid' : `PKR ${Number(test.price || 0).toLocaleString()}`}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="test-actions">
                  {test.already_attempted ? (
                    <button 
                      className="test-button disabled"
                      disabled
                    >
                      Already Attempted
                    </button>
                  ) : test.schedule_status === 'not_started' ? (
                    <button 
                      className="test-button disabled"
                      disabled
                      title={`Test starts in ${formatTimeRemaining(test.time_until_start_seconds)}`}
                    >
                      Not Available Yet
                    </button>
                  ) : test.schedule_status === 'ended' ? (
                    <button 
                      className="test-button disabled"
                      disabled
                      title="Test time has passed"
                    >
                      Time Passed
                    </button>
                  ) : (
                    <button 
                      className="test-button primary"
                      onClick={() => handleTakeTest(test.id, test)}
                    >
                      {test.payment_required && !test.is_paid
                        ? 'Pay Fee to Unlock'
                        : (test.face_detection && !faceRegistered ? 'Register Face & Start' : 'Start Test')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}

export default TestList
